/**
 * Unified Approvals Hub — centralises every pending approval across the ERP.
 * GET /api/approvals/pending returns all items the current user can act on.
 * Approve/Reject calls go to each module's own endpoint (no logic here).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2, XCircle, Clock, AlertTriangle, ChevronDown,
  RefreshCw, Inbox, IndianRupee, User, FolderKanban, Info,
} from 'lucide-react';
import { apiGet, apiPost, apiPut, apiPatch } from '../lib/api';
import { toast } from 'sonner';

// ── helpers ───────────────────────────────────────────────────────────────────
const fmtINR = (n) => n ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : null;

const CATEGORY_COLORS = {
  Procurement: 'bg-teal-50 text-teal-700 border-teal-200',
  HR:          'bg-violet-50 text-violet-700 border-violet-200',
  Finance:     'bg-emerald-50 text-emerald-700 border-emerald-200',
  Safety:      'bg-red-50 text-red-700 border-red-200',
  Labour:      'bg-amber-50 text-amber-700 border-amber-200',
  Admin:       'bg-slate-100 text-slate-600 border-slate-200',
};

const TYPE_ICONS = {
  mpr: '📋', po: '🛒', work_order: '📝', vendor_application: '🏢',
  vendor_change: '🔄', leave: '🏖️', overtime: '⏰', regularization: '🕐',
  mrf: '👥', payment_voucher: '💳', receipt_voucher: '🧾', vendor_invoice: '📄',
  labour_payrun: '💰', client_bill_cert: '📑', petty_cash: '💵',
  permit_to_work: '⛑️', itp_approval: '✅', subcontractor_emp: '🤝', user_approval: '👤',
  travel_request: '✈️', travel_expense: '🧳', reimbursement: '💸',
  hire_log: '🚜', measurement_book: '📐',
  wo_variation: '🔄', marriage_benefit: '💍', material_requisition: '📋',
};

async function callEndpoint(method, endpoint) {
  const m = method?.toLowerCase();
  if (m === 'post')  return apiPost(endpoint, {});
  if (m === 'put')   return apiPut(endpoint, {});
  if (m === 'patch') return apiPatch(endpoint, {});
  return apiGet(endpoint);
}

// ── Reject modal ──────────────────────────────────────────────────────────────
function RejectModal({ item, onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" data-testid="reject-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="font-display font-bold text-lg mb-1">Reject — {item.title}</h3>
        <p className="text-sm text-slate-500 mb-4">Please provide a reason for rejection.</p>
        <textarea
          autoFocus
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          placeholder="Reason for rejection…"
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 resize-none"
          data-testid="reject-reason-input"
        />
        <div className="flex gap-2 mt-4 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="reject-cancel-btn">Cancel</button>
          <button
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason)}
            className="px-4 py-2 text-sm font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-lg disabled:opacity-40"
            data-testid="reject-confirm-btn"
          >
            Confirm Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Single approval card ──────────────────────────────────────────────────────
function ApprovalCard({ item, onAction, onExpand }) {
  const [expanded, setExpanded] = useState(false);
  const [acting, setActing] = useState(null); // 'approve' | 'reject'
  const [showRejectModal, setShowRejectModal] = useState(false);
  const catCls = CATEGORY_COLORS[item.category] || 'bg-slate-100 text-slate-600 border-slate-200';

  // Collapsed by default — matches mobile's correct behaviour.
  // User must explicitly click to expand any card.
  const hasExtras = Object.values(item.extra_fields || {}).some(v => v) || item.has_detail;
  const [userToggled, setUserToggled] = useState(false);
  const isExpanded = userToggled ? expanded : false;
  const [detailLoading, setDetailLoading] = useState(false);

  const handleToggle = async () => {
    const willExpand = !expanded;
    setUserToggled(true);
    setExpanded(willExpand);
    if (willExpand && item.has_detail && !Object.keys(item.extra_fields || {}).length) {
      setDetailLoading(true);
      try { await onExpand?.(item.id); }
      finally { setDetailLoading(false); }
    }
  };

  const handleApprove = async () => {
    if (!item.approve_endpoint) return;
    setActing('approve');
    try {
      await callEndpoint(item.approve_method, item.approve_endpoint);
      toast.success('Approved');
      onAction(item.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve');
    } finally { setActing(null); }
  };

  const handleRejectClick = () => {
    if (item.needs_reason_on_reject) { setShowRejectModal(true); }
    else { doReject(''); }
  };

  const doReject = async (reason) => {
    if (!item.reject_endpoint) return;
    setShowRejectModal(false);
    setActing('reject');
    try {
      const body = reason ? { reason } : {};
      const m = (item.reject_method || 'POST').toLowerCase();
      if (m === 'post')  await apiPost(item.reject_endpoint, body);
      else if (m === 'put') await apiPut(item.reject_endpoint, body);
      else if (m === 'patch') await apiPatch(item.reject_endpoint, body);
      toast.success('Rejected');
      onAction(item.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reject');
    } finally { setActing(null); }
  };

  const extras = Object.entries(item.extra_fields || {}).filter(([, v]) => v);

  return (
    <>
      {showRejectModal && (
        <RejectModal item={item} onConfirm={doReject} onCancel={() => setShowRejectModal(false)} />
      )}
      <div
        className={`bg-white border rounded-xl overflow-hidden transition-shadow hover:shadow-md ${
          item.urgency === 'high' ? 'border-rose-300' : 'border-slate-200'
        }`}
        data-testid={`approval-card-${item.id}`}
      >
        {/* header strip */}
        <div className="flex items-start gap-3 p-4">
          {/* icon */}
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0 ${
            item.urgency === 'high' ? 'bg-rose-50' : 'bg-slate-50'
          }`}>
            {TYPE_ICONS[item.type] || '📌'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
              {item.urgency === 'high' && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
                  <AlertTriangle size={9} /> Urgent
                </span>
              )}
              <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${catCls}`}>
                {item.category}
              </span>
            </div>
            <h3 className="font-display font-bold text-[14px] text-slate-900 leading-tight truncate">{item.title}</h3>
            {item.subtitle && <p className="text-xs text-slate-500 mt-0.5 truncate">{item.subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={handleToggle}
            className="shrink-0 p-1 text-slate-400 hover:text-slate-600"
            data-testid={`card-expand-${item.id}`}
          >
            <ChevronDown size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* meta row */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 px-4 pb-3 text-[11px] text-slate-500">
          {item.project_name && (
            <span className="flex items-center gap-1"><FolderKanban size={10} />{item.project_name}</span>
          )}
          {item.raised_by && (
            <span className="flex items-center gap-1"><User size={10} />{item.raised_by}</span>
          )}
          {item.raised_at && (
            <span className="flex items-center gap-1"><Clock size={10} />{relTime(item.raised_at)}</span>
          )}
          {item.amount > 0 && (
            <span className="flex items-center gap-1 font-semibold text-slate-700">
              <IndianRupee size={10} />{fmtINR(item.amount)}
            </span>
          )}
          {item.detail_url && (
            <a href={item.detail_url} className="flex items-center gap-1 text-[#1F7A6E] font-bold ml-auto hover:underline" data-testid={`card-detail-${item.id}`}>
              View full details →
            </a>
          )}
        </div>

        {/* expanded extras */}
        {isExpanded && (
          detailLoading ? (
            <div className="px-4 pb-3 pt-2 border-t border-slate-100 text-[11px] text-slate-400 flex items-center gap-1.5">
              <RefreshCw size={11} className="animate-spin" /> Loading details…
            </div>
          ) : extras.length > 0 && (
            <div className="px-4 pb-3 space-y-1.5 bg-slate-50/60 border-t border-slate-100 pt-2">
              {extras.map(([k, v]) => (
                <div key={k} className="text-[11px] flex gap-2">
                  <span className="text-slate-400 shrink-0 min-w-[80px]">{k}:</span>
                  <span className="text-slate-700 font-medium whitespace-pre-wrap">{v}</span>
                </div>
              ))}
            </div>
          )
        )}

        {/* action buttons */}
        <div className="flex border-t border-slate-100 divide-x divide-slate-100">
          {item.reject_endpoint && (
            <button
              disabled={!!acting}
              onClick={handleRejectClick}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-display font-bold text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-40"
              data-testid={`reject-btn-${item.id}`}
            >
              {acting === 'reject' ? <RefreshCw size={13} className="animate-spin" /> : <XCircle size={13} />}
              Reject
            </button>
          )}
          {item.approve_endpoint && (
            <button
              disabled={!!acting}
              onClick={handleApprove}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-display font-bold text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40"
              data-testid={`approve-btn-${item.id}`}
            >
              {acting === 'approve' ? <RefreshCw size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              Approve
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ── relative time helper ──────────────────────────────────────────────────────
function relTime(ts) {
  if (!ts) return '';
  try {
    const diff = (Date.now() - new Date(ts).getTime()) / 60000;
    if (diff < 60) return `${Math.floor(diff)}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  } catch { return ts.slice(0, 10); }
}

// ── Main page ─────────────────────────────────────────────────────────────────
const TABS = ['All', 'Procurement', 'HR', 'Finance', 'Safety', 'Labour', 'Admin'];

export default function ApprovalsHub() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('All');
  const [lastRefresh, setLastRefresh] = useState(null);
  // Step 3: detail cache — fetched lazily on card expand
  const [detailCache, setDetailCache] = useState({});  // id → extra_fields

  const load = useCallback(async () => {
    try {
      const r = await apiGet('/approvals/pending');
      setData(r);
      setLastRefresh(new Date());
    } catch (e) {
      toast.error('Could not load approvals');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const onAction = useCallback((id) => {
    setData(prev => prev ? {
      ...prev,
      items: prev.items.filter(i => i.id !== id),
      total: prev.total - 1,
      by_category: recountCategories(prev.items.filter(i => i.id !== id)),
    } : prev);
  }, []);

  const items = (data?.items || []).filter(i => tab === 'All' || i.category === tab);

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-slate-400 text-sm">
      <RefreshCw size={18} className="animate-spin mr-2" /> Loading approvals…
    </div>
  );

  return (
    <div data-testid="approvals-hub-page">
      {/* Page header */}
      <div className="mb-6">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-1">Overview</div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-3">
              Approvals Hub
              {data?.total > 0 && (
                <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-rose-500 text-white text-sm font-bold" data-testid="hub-total-badge">
                  {data.total}
                </span>
              )}
            </h1>
            <p className="text-sm text-slate-500 mt-1">All pending approvals across every module — act without switching pages.</p>
          </div>
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600"
            data-testid="hub-refresh-btn"
          >
            <RefreshCw size={12} /> Refresh
            {lastRefresh && <span className="text-[10px] text-slate-400 font-normal ml-1">({relTime(lastRefresh.toISOString())})</span>}
          </button>
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex gap-1 flex-wrap mb-6 border-b border-slate-200 pb-0">
        {TABS.map(t => {
          const count = t === 'All' ? data?.total : (data?.by_category?.[t] || 0);
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`hub-tab-${t.toLowerCase()}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${
                tab === t
                  ? 'border-[#1F7A6E] text-[#1F7A6E]'
                  : 'border-transparent text-slate-500 hover:text-[#1F7A6E]'
              }`}
            >
              {t}
              {count > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                  tab === t ? 'bg-[#1F7A6E] text-white' : 'bg-slate-200 text-slate-600'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Empty state */}
      {items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400" data-testid="hub-empty-state">
          <Inbox size={40} className="mb-4 text-slate-300" />
          <p className="font-display font-bold text-slate-500">Nothing pending</p>
          <p className="text-sm mt-1">
            {tab === 'All' ? 'No approvals waiting for your action.' : `No pending ${tab} approvals.`}
          </p>
        </div>
      )}

      {/* Cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="hub-cards-grid">
        {items.map(item => (
          <ApprovalCard
            key={item.id}
            item={{
              ...item,
              // Merge in lazily-loaded extra_fields if available
              extra_fields: detailCache[item.id] || item.extra_fields || {},
            }}
            onAction={onAction}
            onExpand={async (id) => {
              if (detailCache[id]) return; // already cached
              const itemData = (data?.items || []).find(i => i.id === id);
              if (!itemData?.has_detail) return; // no detail to fetch
              try {
                const r = await apiGet(`/approvals/${id}/detail`);
                setDetailCache(prev => ({ ...prev, [id]: r.extra_fields || {} }));
              } catch {
                // non-fatal — card still works without detail
              }
            }}
          />
        ))}
      </div>
    </div>
  );
}

function recountCategories(items) {
  const counts = {};
  for (const i of items) counts[i.category] = (counts[i.category] || 0) + 1;
  return counts;
}
